import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Dialog, List, ListItemButton, ListItemText, CircularProgress, Box } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { getBookedSlotStripColor } from 'src/clinic/components/ClinicManageBookingsPage/DayViewCalendarComponent'
import {
  displayNamesOfEventTypes,
  allAvailableEventTypes,
  BOOKED_EVENT_STEP_STATUSES,
} from 'src/consumer/constants'
import { sessionService } from 'src/consumer/components/LiveSessionPage/services/sessionService'

dayjs.extend(utc)

// Short labels for event type when strip height/width is limited (like THRPY for Therapy Session)
const EVENT_TYPE_SHORT_LABELS = {
  [allAvailableEventTypes?.consultation]: 'CONSULT',
  [allAvailableEventTypes?.therapySession]: 'THRPY',
  [allAvailableEventTypes?.screening]: 'SCREEN',
  [allAvailableEventTypes?.assessment]: 'ASSESS',
  'assessment-report': 'ASSESS',
  [allAvailableEventTypes?.oneOnOne]: '1ON1',
  interview: 'INTERV',
  webinar: 'WEB',
}

function getStepStatusChar(status) {
  switch (status) {
    case BOOKED_EVENT_STEP_STATUSES.COMPLETED:
      return '✓'
    case BOOKED_EVENT_STEP_STATUSES.NOT_STARTED:
      return '✗'
    case BOOKED_EVENT_STEP_STATUSES.IN_PROGRESS:
    case BOOKED_EVENT_STEP_STATUSES.STARTED:
      return '○'
    case BOOKED_EVENT_STEP_STATUSES.HANDLER_NOT_ASSIGNED:
    default:
      return '…'
  }
}

function getStepLabelByRefType(refType) {
  return displayNamesOfEventTypes?.[refType] || refType || 'Step'
}

/** Summarise steps: "Screening (✓)" or "Screening (2) (✓/✗)" - show count only if multiple. Not clickable. */
function StepsSummary({ steps, label }) {
  if (!Array.isArray(steps) || steps.length === 0) return null
  const count = steps.length
  const countSuffix = count > 1 ? ` (${count})` : ''
  const summary = steps.map((s) => getStepStatusChar(s?.status)).join('/')
  return (
    <span style={{ fontSize: '9px', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
      {label}{countSuffix} ({summary})
    </span>
  )
}

/** Clickable inSteps summary for mentor: single step → direct launch; multiple → dialog list */
function InStepsSummaryClickable({
  inStepsByType,
  bookedSlotsInDay,
  onGoToEvent,
  getStepLabelByRefType,
  getStepStatusChar,
}) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [loadingRefId, setLoadingRefId] = useState(null)

  const inStepsWithRefId = useMemo(() => {
    const list = []
    Object.entries(inStepsByType || {}).forEach(([refType, steps]) => {
      steps.forEach((step) => {
        if (step?.refId) list.push({ ...step, refType })
      })
    })
    return list
  }, [inStepsByType])

  const resolveBookedEvent = useCallback(
    async (refId) => {
      const fromList = (bookedSlotsInDay || []).find((s) => s?.id === refId)
      if (fromList?.slug) return fromList
      setLoadingRefId(refId)
      try {
        const res = await sessionService.getBookedEventDetails(refId, false)
        if (res?.success && res?.bookedEvent) return res.bookedEvent
      } finally {
        setLoadingRefId(null)
      }
      return null
    },
    [bookedSlotsInDay],
  )

  const handleInStepClick = useCallback(
    async (step) => {
      const refId = step?.refId
      if (!refId || !onGoToEvent) return
      const event = await resolveBookedEvent(refId)
      if (event) onGoToEvent(event)
      setDialogOpen(false)
    },
    [onGoToEvent, resolveBookedEvent],
  )

  if (inStepsWithRefId.length === 0) return null

  const singleStep = inStepsWithRefId.length === 1 ? inStepsWithRefId[0] : null
  const label = singleStep
    ? getStepLabelByRefType(singleStep.refType)
    : 'In-steps'
  const countSuffix = inStepsWithRefId.length > 1 ? ` (${inStepsWithRefId.length})` : ''
  const summary = (singleStep ? [singleStep] : inStepsWithRefId)
    .map((s) => getStepStatusChar(s?.status))
    .join('/')

  const handleClick = (e) => {
    e.stopPropagation()
    if (singleStep) {
      handleInStepClick(singleStep)
    } else {
      setDialogOpen(true)
    }
  }

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleClick(e)
          }
        }}
        style={{
          fontSize: '9px',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        {label}{countSuffix} ({summary})
      </span>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <Box sx={{ p: 2 }}>
          <Box component="h3" sx={{ mt: 0, mb: 2 }}>
            Open in-step session
          </Box>
          <List dense>
            {inStepsWithRefId.map((step, idx) => {
              const refId = step.refId
              const loading = loadingRefId === refId
              const slotFromList = (bookedSlotsInDay || []).find((s) => s?.id === refId)
              const sub = slotFromList
                ? `${slotFromList?.patientInfo?.name || 'Patient'} · ${dayjs.utc(slotFromList?.startTime).local().format('h:mm A')}`
                : refId
              return (
                <ListItemButton
                  key={step?.id || refId || idx}
                  onClick={() => handleInStepClick(step)}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  ) : null}
                  <ListItemText
                    primary={`${getStepLabelByRefType(step.refType)} (${getStepStatusChar(step?.status)})`}
                    secondary={sub}
                  />
                </ListItemButton>
              )
            })}
          </List>
        </Box>
      </Dialog>
    </>
  )
}

export default function EventTypeAndStepsStrip({
  bookedSlot,
  slotBlocked,
  isHorizontal = false,
  forMentor = false,
  bookedSlotsInDay,
  onGoToEvent,
}) {
  const stripRef = useRef(null)
  const [useShortEventLabel, setUseShortEventLabel] = useState(false)

  const eventType = bookedSlot?.eventType
  const eventTypeDisplayName = displayNamesOfEventTypes?.[eventType] || eventType || 'Session'
  const eventTypeShortLabel = EVENT_TYPE_SHORT_LABELS[eventType] || eventTypeDisplayName?.slice(0, 6)?.toUpperCase() || 'SESSION'

  const preSteps = Array.isArray(bookedSlot?.previousSteps) ? bookedSlot.previousSteps : []
  const inSteps = Array.isArray(bookedSlot?.nextSteps) ? bookedSlot.nextSteps : []

  const preStepsByType = preSteps.reduce((acc, step) => {
    const type = step?.refType || 'step'
    if (!acc[type]) acc[type] = []
    acc[type].push(step)
    return acc
  }, {})
  const inStepsByType = inSteps.reduce((acc, step) => {
    const type = step?.refType || 'step'
    if (!acc[type]) acc[type] = []
    acc[type].push(step)
    return acc
  }, {})

  const inStepsWithRefIdCount = inSteps.filter((s) => s?.refId).length

  useEffect(() => {
    if (stripRef.current && !isHorizontal) {
      const checkSpace = () => {
        const height = stripRef.current?.offsetHeight || 0
        const width = stripRef.current?.offsetWidth || 0
        setUseShortEventLabel(height < 90 || width < 30)
      }
      checkSpace()
      const ro = new ResizeObserver(checkSpace)
      if (stripRef.current) ro.observe(stripRef.current)
      return () => ro.disconnect()
    }
  }, [isHorizontal])

  const colors = getBookedSlotStripColor(bookedSlot, slotBlocked)
  const stripStyle = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 6px',
    marginRight: '8px',
    minWidth: '32px',
    borderRight: `2px solid ${colors?.eventTypeStripBorderColor ?? colors?.inTherapyBorderColor}`,
    background: colors?.eventTypeStripColor ?? colors?.inTherapyColor,
    color: colors?.eventTypeStripTextColor ?? colors?.inTherapyTextColor,
    borderRadius: '0 4px 4px 0',
    boxShadow: 'inset -2px 0 4px rgba(0, 0, 0, 0.05)',
  }

  const textStyle = {
    transform: isHorizontal ? 'none' : 'rotate(180deg)',
    writingMode: isHorizontal ? 'unset' : 'vertical-lr',
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    lineHeight: 1.2,
    padding: isHorizontal ? 0 : '0 2px',
    textAlign: 'center',
  }

  const eventLabel = useShortEventLabel ? eventTypeShortLabel : eventTypeDisplayName

  const showClickableInSteps = forMentor && onGoToEvent && inStepsWithRefIdCount > 0

  return (
    <div ref={stripRef} style={stripStyle}>
      <span style={textStyle}>
        <span>{eventLabel}</span>
        {Object.keys(preStepsByType).length > 0 && (
          <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Object.entries(preStepsByType).map(([refType, steps]) => (
              <StepsSummary key={`pre-${refType}`} steps={steps} label={getStepLabelByRefType(refType)} />
            ))}
          </span>
        )}
        {Object.keys(inStepsByType).length > 0 && (
          <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {showClickableInSteps ? (
              <InStepsSummaryClickable
                inStepsByType={inStepsByType}
                bookedSlotsInDay={bookedSlotsInDay}
                onGoToEvent={onGoToEvent}
                getStepLabelByRefType={getStepLabelByRefType}
                getStepStatusChar={getStepStatusChar}
              />
            ) : (
              Object.entries(inStepsByType).map(([refType, steps]) => (
                <StepsSummary key={`in-${refType}`} steps={steps} label={getStepLabelByRefType(refType)} />
              ))
            )}
          </span>
        )}
      </span>
    </div>
  )
}
